// ─────────────────────────────────────────────
//  loginFacility — which board should open when you sign in?
//
//  THE PROBLEM. Every new profile is bootstrapped with
//  activeFacilityId = DEFAULT_FACILITY_ID (config/roles.js), and after that the
//  value only ever changes when somebody uses the NavMenu facility switcher. So
//  a person hired at Tampa signs in to Wesley Chapel's board; a CRNA floated to
//  Carrollwood for the day sees the wrong campus until they notice the switcher
//  exists. The app knows where they are working — it just never used it.
//
//  RESOLUTION ORDER
//    1. Today's assignment. If they are staffed in a room at a facility, open
//       that one. Already on one of them (a split day) → leave it alone; being
//       yanked to the other campus at every sign-in is worse than a stale pick.
//    2. Otherwise, if the stored facility is one they cannot even access, correct
//       it — preferring their home facility, else the first they can reach.
//    3. Otherwise change nothing.
//
//  HARD RULE: never return a facility outside the user's own facilityIds, even
//  when an assignment names it. The security rules would reject the reads and
//  the app would look broken rather than merely mis-scoped.
//
//  Applied ONCE per sign-in (see AuthContext), never on every render — a manual
//  switch mid-session has to survive.
//
//  Pure — no Firebase.
// ─────────────────────────────────────────────

const list = (v) => (Array.isArray(v) ? v.filter(Boolean) : []);

/**
 * @param {object} o
 *   @param {string} [o.currentFacilityId]   — users/{uid}.activeFacilityId today
 *   @param {string[]} [o.facilityIds]       — facilities this user may access
 *   @param {string[]} [o.assignedFacilityIds] — facilities where they hold a room today
 *   @param {string} [o.primaryFacilityId]   — their home facility, if known
 * @returns {{facilityId: string|null, reason: string, changed: boolean}}
 */
export function resolveLoginFacility({
  currentFacilityId = null,
  facilityIds = [],
  assignedFacilityIds = [],
  primaryFacilityId = null,
} = {}) {
  const allowed = list(facilityIds);
  const current = currentFacilityId || null;
  const unchanged = { facilityId: current, reason: "unchanged", changed: false };

  // No declared scope — do not guess a facility for someone whose access has
  // not been set up. They keep whatever board they had.
  if (!allowed.length) return unchanged;

  // 1. Where are they actually working today? Intersected with what they may
  //    read, in the user's own facility order so the pick is deterministic.
  const assigned = allowed.filter((fid) => list(assignedFacilityIds).includes(fid));
  if (assigned.length) {
    if (current && assigned.includes(current)) return unchanged;
    return { facilityId: assigned[0], reason: "assignment", changed: assigned[0] !== current };
  }

  // 2. Is the stored facility even usable? The bootstrap default is the same
  //    for everyone, so this is the common case for a new hire.
  if (!current || !allowed.includes(current)) {
    const home = primaryFacilityId && allowed.includes(primaryFacilityId)
      ? primaryFacilityId
      : allowed[0];
    return { facilityId: home, reason: "out_of_scope", changed: home !== current };
  }

  return unchanged;
}
